import { supabase } from '../lib/supabase';

export const payoffPlanService = {
  // Get all plans for a specific debt
  async getAllForDebt(debtId) {
    try {
      const { data, error } = await supabase
        ?.from('debt_payoff_plans')
        ?.select('*')
        ?.eq('debt_id', debtId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        debtId: row?.debt_id,
        planName: row?.plan_name,
        strategy: row?.strategy,
        extraMonthlyPayment: parseFloat(row?.extra_monthly_payment || 0),
        oneTimePayment: parseFloat(row?.one_time_payment || 0),
        extraRecurringPayment: parseFloat(row?.extra_recurring_payment || 0),
        focusedDebtId: row?.focused_debt_id,
        targetPayoffDate: row?.target_payoff_date,
        monthsToPayoff: row?.months_to_payoff,
        totalInterest: parseFloat(row?.total_interest),
        interestSaved: parseFloat(row?.interest_saved || 0),
        timeSavedMonths: row?.time_saved_months || 0,
        projectedPayoffDate: row?.projected_payoff_date,
        planDetails: row?.plan_details,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching payoff plans:', error);
      throw error;
    }
  },

  // Get a single plan by ID
  async getById(planId) {
    try {
      const { data, error } = await supabase
        ?.from('debt_payoff_plans')
        ?.select('*')
        ?.eq('id', planId)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        debtId: data?.debt_id,
        planName: data?.plan_name,
        strategy: data?.strategy,
        extraMonthlyPayment: parseFloat(data?.extra_monthly_payment || 0),
        oneTimePayment: parseFloat(data?.one_time_payment || 0),
        extraRecurringPayment: parseFloat(data?.extra_recurring_payment || 0),
        focusedDebtId: data?.focused_debt_id,
        targetPayoffDate: data?.target_payoff_date,
        monthsToPayoff: data?.months_to_payoff,
        totalInterest: parseFloat(data?.total_interest),
        interestSaved: parseFloat(data?.interest_saved || 0),
        timeSavedMonths: data?.time_saved_months || 0,
        projectedPayoffDate: data?.projected_payoff_date,
        planDetails: data?.plan_details,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching payoff plan:', error);
      throw error;
    }
  },

  // Create a new payoff plan
  async create(planData) {
    try {
      const { data, error } = await supabase
        ?.from('debt_payoff_plans')
        ?.insert({
          debt_id: planData?.debtId,
          plan_name: planData?.planName,
          strategy: planData?.strategy,
          extra_monthly_payment: planData?.extraMonthlyPayment || 0,
          one_time_payment: planData?.oneTimePayment || 0,
          extra_recurring_payment: planData?.extraRecurringPayment || 0,
          focused_debt_id: planData?.focusedDebtId || null,
          target_payoff_date: planData?.targetPayoffDate || null,
          months_to_payoff: planData?.monthsToPayoff,
          total_interest: planData?.totalInterest,
          interest_saved: planData?.interestSaved || 0,
          time_saved_months: planData?.timeSavedMonths || 0,
          projected_payoff_date: planData?.projectedPayoffDate,
          plan_details: planData?.planDetails || {}
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        debtId: data?.debt_id,
        planName: data?.plan_name,
        strategy: data?.strategy,
        extraMonthlyPayment: parseFloat(data?.extra_monthly_payment || 0),
        oneTimePayment: parseFloat(data?.one_time_payment || 0),
        extraRecurringPayment: parseFloat(data?.extra_recurring_payment || 0),
        focusedDebtId: data?.focused_debt_id,
        targetPayoffDate: data?.target_payoff_date,
        monthsToPayoff: data?.months_to_payoff,
        totalInterest: parseFloat(data?.total_interest),
        interestSaved: parseFloat(data?.interest_saved || 0),
        timeSavedMonths: data?.time_saved_months || 0,
        projectedPayoffDate: data?.projected_payoff_date,
        planDetails: data?.plan_details,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating payoff plan:', error);
      throw error;
    }
  },

  // Update an existing plan
  async update(planId, updates) {
    try {
      const updateData = {};
      if (updates?.planName !== undefined) updateData.plan_name = updates?.planName;
      if (updates?.strategy !== undefined) updateData.strategy = updates?.strategy;
      if (updates?.extraMonthlyPayment !== undefined) updateData.extra_monthly_payment = updates?.extraMonthlyPayment;
      if (updates?.oneTimePayment !== undefined) updateData.one_time_payment = updates?.oneTimePayment;
      if (updates?.extraRecurringPayment !== undefined) updateData.extra_recurring_payment = updates?.extraRecurringPayment;
      if (updates?.focusedDebtId !== undefined) updateData.focused_debt_id = updates?.focusedDebtId;
      if (updates?.targetPayoffDate !== undefined) updateData.target_payoff_date = updates?.targetPayoffDate;
      if (updates?.monthsToPayoff !== undefined) updateData.months_to_payoff = updates?.monthsToPayoff;
      if (updates?.totalInterest !== undefined) updateData.total_interest = updates?.totalInterest;
      if (updates?.interestSaved !== undefined) updateData.interest_saved = updates?.interestSaved;
      if (updates?.timeSavedMonths !== undefined) updateData.time_saved_months = updates?.timeSavedMonths;
      if (updates?.projectedPayoffDate !== undefined) updateData.projected_payoff_date = updates?.projectedPayoffDate;
      if (updates?.planDetails !== undefined) updateData.plan_details = updates?.planDetails;

      const { data, error } = await supabase
        ?.from('debt_payoff_plans')
        ?.update(updateData)
        ?.eq('id', planId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        debtId: data?.debt_id,
        planName: data?.plan_name,
        strategy: data?.strategy,
        extraMonthlyPayment: parseFloat(data?.extra_monthly_payment || 0),
        oneTimePayment: parseFloat(data?.one_time_payment || 0),
        extraRecurringPayment: parseFloat(data?.extra_recurring_payment || 0),
        focusedDebtId: data?.focused_debt_id,
        targetPayoffDate: data?.target_payoff_date,
        monthsToPayoff: data?.months_to_payoff,
        totalInterest: parseFloat(data?.total_interest),
        interestSaved: parseFloat(data?.interest_saved || 0),
        timeSavedMonths: data?.time_saved_months || 0,
        projectedPayoffDate: data?.projected_payoff_date,
        planDetails: data?.plan_details,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating payoff plan:', error);
      throw error;
    }
  },

  // Delete a plan
  async delete(planId) {
    try {
      const { error } = await supabase
        ?.from('debt_payoff_plans')
        ?.delete()
        ?.eq('id', planId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting payoff plan:', error);
      throw error;
    }
  },

  // Compare multiple plans
  async comparePlans(planIds) {
    try {
      const { data, error } = await supabase
        ?.rpc('compare_payoff_plans', { p_plan_ids: planIds });

      if (error) throw error;

      return data?.map(row => ({
        planId: row?.plan_id,
        planName: row?.plan_name,
        monthsToPayoff: row?.months_to_payoff,
        totalInterest: parseFloat(row?.total_interest),
        interestSaved: parseFloat(row?.interest_saved || 0),
        timeSavedMonths: row?.time_saved_months || 0,
        rank: row?.rank
      }));
    } catch (error) {
      console.error('Error comparing plans:', error);
      throw error;
    }
  },

  // Get all plans for multiple debts (for multi-debt scenarios)
  async getAllForMultipleDebts(debtIds) {
    try {
      const { data, error } = await supabase
        ?.from('debt_payoff_plans')
        ?.select('*')
        ?.in('debt_id', debtIds)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        debtId: row?.debt_id,
        planName: row?.plan_name,
        strategy: row?.strategy,
        extraMonthlyPayment: parseFloat(row?.extra_monthly_payment || 0),
        oneTimePayment: parseFloat(row?.one_time_payment || 0),
        extraRecurringPayment: parseFloat(row?.extra_recurring_payment || 0),
        focusedDebtId: row?.focused_debt_id,
        targetPayoffDate: row?.target_payoff_date,
        monthsToPayoff: row?.months_to_payoff,
        totalInterest: parseFloat(row?.total_interest),
        interestSaved: parseFloat(row?.interest_saved || 0),
        timeSavedMonths: row?.time_saved_months || 0,
        projectedPayoffDate: row?.projected_payoff_date,
        planDetails: row?.plan_details,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching multi-debt payoff plans:', error);
      throw error;
    }
  }
};